import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useTranslation } from '@/app/providers';
import { cn } from '@/shared/lib/cn';
import 'flag-icons/css/flag-icons.min.css';

interface LanguageSelectorProps {
  variant?: 'light' | 'dark';
  /** Compact circular flag (default) or labeled button with chevron */
  appearance?: 'icon' | 'labeled';
}

export function LanguageSelector({
  variant = 'light',
  appearance = 'icon',
}: LanguageSelectorProps) {
  const { locale, setLocale, localeOptions, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!isOpen || !buttonRef.current) {
      return;
    }

    const updatePosition = () => {
      if (!buttonRef.current) {
        return;
      }

      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 176;
      const padding = 12;

      let x = rect.left;
      if (x + dropdownWidth > window.innerWidth - padding) {
        x = window.innerWidth - dropdownWidth - padding;
      }
      if (x < padding) {
        x = padding;
      }

      setMenuPosition({
        top: rect.bottom + 8,
        left: x,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  const current = localeOptions.find((option) => option.code === locale);
  const isDark = variant === 'dark';
  const isLabeled = appearance === 'labeled';

  const menu =
    isOpen && typeof document !== 'undefined'
      ? createPortal(
          <>
            <div
              className="fixed inset-0 z-[200]"
              onClick={() => {
                setIsOpen(false);
              }}
              aria-hidden="true"
            />
            <div
              className="fixed z-[210] w-44 rounded-xl border border-border bg-card py-1 shadow-lg"
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
              }}
              role="listbox"
              aria-label={t('header.language')}
            >
              {localeOptions.map((option) => (
                <button
                  key={option.code}
                  type="button"
                  role="option"
                  aria-selected={locale === option.code}
                  onClick={() => {
                    setLocale(option.code);
                    setIsOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors first:rounded-t-xl last:rounded-b-xl hover:bg-muted',
                    locale === option.code
                      ? 'font-medium text-primary'
                      : 'text-body',
                  )}
                >
                  <span
                    className={`${option.flag} shrink-0 rounded-none`}
                    style={{
                      width: '20px',
                      height: '15px',
                    }}
                    aria-hidden="true"
                  />
                  {option.label}
                </button>
              ))}
            </div>
          </>,
          document.body,
        )
      : null;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        className={cn(
          'language-selector-trigger flex items-center transition-colors',
          isLabeled
            ? 'gap-2 rounded-lg border px-2.5 py-1.5 text-sm'
            : 'justify-center rounded-full border text-lg',
          isDark
            ? 'border-surface-border text-surface-muted hover:bg-surface-hover hover:text-white'
            : 'border-content-border bg-card text-body hover:bg-muted',
        )}
        aria-label={t('header.language')}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={() => {
          setIsOpen((prev) => !prev);
        }}
      >
        {current ? (
          <span
            className={cn(
              current.flag,
              'bg-cover',
              isLabeled ? 'h-4 w-5 rounded-sm' : 'h-6 w-6 rounded-full',
            )}
          />
        ) : (
          <span>🌐</span>
        )}
        {isLabeled && (
          <>
            <span className="font-medium">{current?.label}</span>
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 text-secondary transition-transform',
                isOpen && 'rotate-180',
              )}
              aria-hidden
            />
          </>
        )}
      </button>
      {menu}
    </div>
  );
}
